<template>
  <div class="p-6">
    <div class="mb-6">
      <h1 class="text-2xl font-bold text-gray-900 dark:text-white mb-2">
        Asignación Masiva de Usuarios a Negocios
      </h1>
      <p class="text-gray-600 dark:text-gray-400">
        Selecciona múltiples usuarios y asígnalos a uno o varios negocios de forma masiva
      </p>
    </div>

    <!-- Filtros y controles principales -->
    <div class="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 mb-6">
      <div class="p-6">
        <div class="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <!-- Filtro de usuarios -->
          <div>
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Buscar usuarios
            </label>
            <div class="relative">
              <input 
                v-model="userSearch" 
                type="text" 
                placeholder="Nombre, email o username..." 
                class="w-full px-4 py-2 pl-10 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 dark:bg-gray-700 dark:text-white"
              />
              <svg class="w-5 h-5 absolute left-3 top-2.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
              </svg>
            </div>
          </div>

          <!-- Filtro por rol -->
          <div>
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Filtrar por rol
            </label>
            <select 
              v-model="roleFilter"
              class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 dark:bg-gray-700 dark:text-white"
            >
              <option value="">Todos los roles</option>
              <option v-for="role in roles" :key="role.id" :value="role.id">
                {{ role.nombre }}
              </option>
            </select>
          </div>

          <!-- Filtro por negocio actual -->
          <div>
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Mostrar usuarios
            </label>
            <select 
              v-model="assignmentFilter"
              class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 dark:bg-gray-700 dark:text-white"
            >
              <option value="all">Todos</option>
              <option value="assigned">Solo asignados</option>
              <option value="unassigned">Solo sin asignar</option>
            </select>
          </div>
        </div>

        <!-- Controles de selección -->
        <div class="flex flex-wrap items-center justify-between gap-4">
          <div class="flex items-center gap-4">
            <button 
              @click="selectAll"
              class="text-violet-600 hover:text-violet-700 font-medium text-sm"
            >
              Seleccionar todos ({{ filteredUsers.length }})
            </button>
            <button 
              @click="selectNone"
              class="text-gray-600 hover:text-gray-700 font-medium text-sm"
            >
              Deseleccionar todos
            </button>
            <span v-if="selectedUsers.length > 0" class="text-sm text-gray-600 dark:text-gray-400">
              {{ selectedUsers.length }} usuario(s) seleccionado(s)
            </span>
          </div>

          <!-- Acciones masivas -->
          <div class="flex items-center gap-2" v-if="selectedUsers.length > 0">
            <button
              @click="showBulkAssignModal = true"
              class="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg transition-colors duration-200 flex items-center gap-2"
            >
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"/>
              </svg>
              Asignar a Negocios
            </button>
            <button
              @click="showBulkRemoveModal = true"
              class="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg transition-colors duration-200 flex items-center gap-2"
            >
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
              </svg>
              Remover de Negocios
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Lista de usuarios con checkboxes -->
    <div class="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
      <div class="p-6 border-b border-gray-200 dark:border-gray-700">
        <h2 class="text-lg font-semibold text-gray-900 dark:text-white">
          Lista de Usuarios ({{ filteredUsers.length }})
        </h2>
      </div>

      <div class="p-6">
        <div v-if="loadingUsers" class="text-center py-8">
          <svg class="animate-spin h-8 w-8 text-violet-600 mx-auto" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"></path>
          </svg>
          <p class="text-gray-500 mt-2">Cargando usuarios...</p>
        </div>

        <div v-else-if="filteredUsers.length === 0" class="text-center py-8 text-gray-500">
          No se encontraron usuarios con los filtros aplicados
        </div>

        <div v-else class="space-y-3">
          <div
            v-for="user in filteredUsers"
            :key="user.id"
            :class="[
              'flex items-center p-4 rounded-lg border-2 transition-all duration-200 cursor-pointer',
              selectedUsers.includes(user.id)
                ? 'bg-violet-50 dark:bg-violet-900/20 border-violet-300 dark:border-violet-600'
                : 'bg-gray-50 dark:bg-gray-700 border-gray-200 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-600'
            ]"
            @click="toggleUserSelection(user.id)"
          >
            <!-- Checkbox -->
            <div class="flex-shrink-0 mr-4">
              <input
                type="checkbox"
                :checked="selectedUsers.includes(user.id)"
                @click.stop
                @change="toggleUserSelection(user.id)"
                class="h-4 w-4 text-violet-600 focus:ring-violet-500 border-gray-300 rounded"
              />
            </div>

            <!-- Información del usuario -->
            <div class="flex-1 min-w-0">
              <div class="flex items-center gap-4">
                <div class="flex-1">
                  <h3 class="font-medium text-gray-900 dark:text-white">
                    {{ user.nombre }} {{ user.apellidoPaterno }} {{ user.apellidoMaterno }}
                  </h3>
                  <p class="text-sm text-gray-600 dark:text-gray-400">
                    {{ user.email }} • @{{ user.username }}
                  </p>
                </div>
                
                <!-- Badge de rol -->
                <RolUsuario 
                  :rol-id="user.rol?.id || 0"
                  :nombre="user.rol?.nombre || 'Sin rol'"
                  class="flex-shrink-0"
                />

                <!-- Negocios asignados -->
                <div class="flex flex-wrap gap-1">
                  <span
                    v-for="negocio in user.negocios"
                    :key="negocio.id"
                    class="bg-blue-100 text-blue-800 text-xs font-medium px-2 py-1 rounded-full dark:bg-blue-900 dark:text-blue-300"
                  >
                    {{ negocio.nombre }}
                  </span>
                  <span 
                    v-if="user.negocios?.length === 0"
                    class="bg-gray-100 text-gray-600 text-xs font-medium px-2 py-1 rounded-full dark:bg-gray-700 dark:text-gray-400"
                  >
                    Sin asignar
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal para asignación masiva -->
    <div v-if="showBulkAssignModal" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div class="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-lg w-full mx-4">
        <div class="p-6 border-b border-gray-200 dark:border-gray-700">
          <div class="flex justify-between items-center">
            <h3 class="text-lg font-semibold text-gray-900 dark:text-white">
              Asignar Usuarios a Negocios
            </h3>
            <button @click="closeBulkAssignModal" class="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
              <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
              </svg>
            </button>
          </div>
        </div>

        <form @submit.prevent="bulkAssignUsers" class="p-6 space-y-4">
          <div class="bg-blue-50 dark:bg-blue-900/20 p-3 rounded-lg">
            <p class="text-sm text-blue-700 dark:text-blue-300">
              <strong>{{ selectedUsers.length }}</strong> usuario(s) seleccionado(s)
            </p>
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Seleccionar Negocios *
            </label>
            <div class="max-h-48 overflow-y-auto border border-gray-300 dark:border-gray-600 rounded-md p-3">
              <div v-for="negocio in negocios" :key="negocio.id" class="flex items-center mb-2">
                <input
                  type="checkbox"
                  :id="`negocio-${negocio.id}`"
                  v-model="selectedNegocios"
                  :value="negocio.id"
                  class="h-4 w-4 text-violet-600 focus:ring-violet-500 border-gray-300 rounded"
                />
                <label 
                  :for="`negocio-${negocio.id}`" 
                  class="ml-2 text-sm text-gray-700 dark:text-gray-300 cursor-pointer"
                >
                  {{ negocio.nombre }}
                  <span :class="negocio.activo ? 'text-green-600' : 'text-red-600'" class="text-xs">
                    ({{ negocio.activo ? 'Activo' : 'Inactivo' }})
                  </span>
                </label>
              </div>
            </div>
          </div>

          <div class="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              @click="closeBulkAssignModal"
              class="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-200 dark:bg-gray-700 rounded-md hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
            >
              Cancelar
            </button>
            <button
              type="submit"
              :disabled="loading || selectedNegocios.length === 0"
              class="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
            >
              <svg v-if="loading" class="animate-spin h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"></path>
              </svg>
              {{ loading ? 'Asignando...' : 'Asignar Usuarios' }}
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- Modal para remoción masiva -->
    <div v-if="showBulkRemoveModal" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div class="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-lg w-full mx-4">
        <div class="p-6 border-b border-gray-200 dark:border-gray-700">
          <div class="flex justify-between items-center">
            <h3 class="text-lg font-semibold text-gray-900 dark:text-white">
              Remover Usuarios de Negocios
            </h3>
            <button @click="closeBulkRemoveModal" class="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
              <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
              </svg>
            </button>
          </div>
        </div>

        <form @submit.prevent="bulkRemoveUsers" class="p-6 space-y-4">
          <div class="bg-red-50 dark:bg-red-900/20 p-3 rounded-lg">
            <p class="text-sm text-red-700 dark:text-red-300">
              <strong>{{ selectedUsers.length }}</strong> usuario(s) seleccionado(s)
            </p>
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Seleccionar Negocios a Remover *
            </label>
            <div class="max-h-48 overflow-y-auto border border-gray-300 dark:border-gray-600 rounded-md p-3">
              <div v-for="negocio in negocios" :key="negocio.id" class="flex items-center mb-2">
                <input
                  type="checkbox"
                  :id="`remove-negocio-${negocio.id}`"
                  v-model="selectedNegociosRemove"
                  :value="negocio.id"
                  class="h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 rounded"
                />
                <label 
                  :for="`remove-negocio-${negocio.id}`" 
                  class="ml-2 text-sm text-gray-700 dark:text-gray-300 cursor-pointer"
                >
                  {{ negocio.nombre }}
                </label>
              </div>
            </div>
          </div>

          <div class="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              @click="closeBulkRemoveModal"
              class="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-200 dark:bg-gray-700 rounded-md hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
            >
              Cancelar
            </button>
            <button
              type="submit"
              :disabled="loading || selectedNegociosRemove.length === 0"
              class="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
            >
              <svg v-if="loading" class="animate-spin h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"></path>
              </svg>
              {{ loading ? 'Removiendo...' : 'Remover Usuarios' }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useHttp } from '@/composables/useHttp'
import RolUsuario from '@/components/RolUsuario.vue'
import Swal from 'sweetalert2'

// Referencias reactivas
const users = ref([])
const negocios = ref([])
const roles = ref([])
const selectedUsers = ref([])
const selectedNegocios = ref([])
const selectedNegociosRemove = ref([])
const userSearch = ref('')
const roleFilter = ref('')
const assignmentFilter = ref('all')
const showBulkAssignModal = ref(false)
const showBulkRemoveModal = ref(false)

// Estados de carga
const loadingUsers = ref(false)

// Composable HTTP
const { get, post, del, loading, error, data } = useHttp()

// Computed para filtrar usuarios
const filteredUsers = computed(() => {
  let filtered = users.value

  // Filtro por búsqueda
  if (userSearch.value) {
    const search = userSearch.value.toLowerCase()
    filtered = filtered.filter(user =>
      user.nombre?.toLowerCase().includes(search) ||
      user.apellidoPaterno?.toLowerCase().includes(search) ||
      user.apellidoMaterno?.toLowerCase().includes(search) ||
      user.email?.toLowerCase().includes(search) ||
      user.username?.toLowerCase().includes(search)
    )
  }

  // Filtro por rol
  if (roleFilter.value) {
    filtered = filtered.filter(user => user.rol?.id == roleFilter.value)
  }

  // Filtro por asignación
  if (assignmentFilter.value === 'assigned') {
    filtered = filtered.filter(user => user.negocios && user.negocios.length > 0)
  } else if (assignmentFilter.value === 'unassigned') {
    filtered = filtered.filter(user => !user.negocios || user.negocios.length === 0)
  }

  return filtered
})

// Funciones para cargar datos
const fetchUsers = async () => {
  loadingUsers.value = true
  try {
    await get('users-with-negocios') // Endpoint que incluye los negocios del usuario
    users.value = Array.isArray(data.value) ? data.value : data.value?.data || []
  } catch (err) {
    console.error('Error al cargar usuarios:', err)
    users.value = []
  } finally {
    loadingUsers.value = false
  }
}

const fetchNegocios = async () => {
  try {
    await get('negocios')
    negocios.value = Array.isArray(data.value) ? data.value : data.value?.data || []
  } catch (err) {
    console.error('Error al cargar negocios:', err)
    negocios.value = []
  }
}

const fetchRoles = async () => {
  try {
    await get('roles')
    roles.value = Array.isArray(data.value) ? data.value : data.value?.data || []
  } catch (err) {
    console.error('Error al cargar roles:', err)
    roles.value = []
  }
}

// Funciones de selección
const toggleUserSelection = (userId) => {
  const index = selectedUsers.value.indexOf(userId)
  if (index > -1) {
    selectedUsers.value.splice(index, 1)
  } else {
    selectedUsers.value.push(userId)
  }
}

const selectAll = () => {
  selectedUsers.value = filteredUsers.value.map(user => user.id)
}

const selectNone = () => {
  selectedUsers.value = []
}

// Funciones de modal
const closeBulkAssignModal = () => {
  showBulkAssignModal.value = false
  selectedNegocios.value = []
}

const closeBulkRemoveModal = () => {
  showBulkRemoveModal.value = false
  selectedNegociosRemove.value = []
}

// Operaciones masivas
const bulkAssignUsers = async () => {
  try {
    const assignments = []
    
    // Crear todas las combinaciones usuario-negocio
    for (const userId of selectedUsers.value) {
      for (const negocioId of selectedNegocios.value) {
        assignments.push({
          usuario_id: userId,
          negocio_id: negocioId
        })
      }
    }

    await post('usuario-negocio/bulk-assign', { assignments })
    
    closeBulkAssignModal()
    selectedUsers.value = []
    await fetchUsers()
    
    Swal.fire({
      icon: 'success',
      title: 'Asignación completada',
      text: `Se asignaron ${selectedUsers.value.length} usuarios a ${selectedNegocios.value.length} negocio(s).`,
      timer: 3000,
      showConfirmButton: false
    })
  } catch (err) {
    Swal.fire({
      icon: 'error',
      title: 'Error',
      text: error.value || 'Error al realizar la asignación masiva'
    })
  }
}

const bulkRemoveUsers = async () => {
  const result = await Swal.fire({
    title: '¿Confirmar remoción masiva?',
    text: `Se removerán ${selectedUsers.value.length} usuario(s) de ${selectedNegociosRemove.value.length} negocio(s).`,
    icon: 'warning',
    showCancelButton: true,
    confirmButtonColor: '#d33',
    cancelButtonColor: '#3085d6',
    confirmButtonText: 'Sí, remover',
    cancelButtonText: 'Cancelar'
  })

  if (result.isConfirmed) {
    try {
      const removals = []
      
      // Crear todas las combinaciones usuario-negocio para remover
      for (const userId of selectedUsers.value) {
        for (const negocioId of selectedNegociosRemove.value) {
          removals.push({
            usuario_id: userId,
            negocio_id: negocioId
          })
        }
      }

      await post('usuario-negocio/bulk-remove', { removals })
      
      closeBulkRemoveModal()
      selectedUsers.value = []
      await fetchUsers()
      
      Swal.fire({
        title: '¡Remoción completada!',
        text: 'Los usuarios han sido removidos de los negocios seleccionados.',
        icon: 'success',
        timer: 2000,
        showConfirmButton: false
      })
    } catch (err) {
      Swal.fire('Error', 'No se pudo completar la remoción masiva.', 'error')
    }
  }
}


// Cargar datos al montar el componente
onMounted(() => {
  fetchUsers()
  fetchNegocios()
  fetchRoles()
})
</script>